import Axios from '../../../../axios'
import { Link } from 'react-router-dom'
import MUIDataTable from 'mui-datatables'
import { Breadcrumb } from '../../../components'
import React, { useState, useEffect, useCallback } from 'react'
import { 
    Chip, 
    Grow, 
    Icon, 
    IconButton, 
    TextField, 
    Tooltip, 
    Alert,
    Skeleton,
    Typography,
    CircularProgress
} from '@mui/material'
import { Box, styled } from '@mui/system'
import { API_URI } from '../../../../config'
import EmailIcon from '@mui/icons-material/Email'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'

const FlexBox = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
}))

const Container = styled('div')(({ theme }) => ({
    margin: '30px',
    [theme.breakpoints.down('sm')]: {
        margin: '16px',
    },
    '& .breadcrumb': {
        marginBottom: '30px',
        [theme.breakpoints.down('sm')]: {
            marginBottom: '16px',
        },
    },
}))

const CustomerAwaitingList = ({ condition }) => {
    const [tableConfig, setTableConfig] = useState({
        page: 0,
        count: 1,
        rowsPerPage: 10,
    })
    const [searchValue, setSearchValue] = useState('')
    const [userList, setUserList] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [searchLoading, setSearchLoading] = useState(false)
    
    const getData = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            
            const { data } = await Axios.get(
                `${API_URI}/api/users/awaiting-completion/?page=${tableConfig.page}&rowsPerpage=${tableConfig.rowsPerPage}`
            )
            
            const newUserList = data?.user?.map((item) => ({
                ...item,
                has_email: item?.account_health?.has_email,
                has_assets: item?.account_health?.has_assets
            })) || []
            
            setUserList(newUserList)
            setTableConfig(prev => ({ ...prev, count: data.total || 0 }))
            
        } catch (error) {
            console.error('Error fetching data:', error)
            setError(
                error?.error ||
                error?.message ||
                'Failed to load member data. Please try again.'
            )
        } finally {
            setLoading(false)
        }
    }, [tableConfig.page, tableConfig.rowsPerPage])

    const fetchSearchedValue = useCallback(async () => {
        if (!searchValue.trim()) return
        
        try {
            setSearchLoading(true)
            setError(null)
            
            const { data } = await Axios.get(
                `${API_URI}/api/user-find/get/inComplete/${searchValue}`
            )
            
            const searchResults = data?.map((item) => ({
                ...item,
                has_email: item?.account_health?.has_email,
                has_assets: item?.account_health?.has_assets
            })) || []
            
            setUserList(searchResults)
            setTableConfig(prev => ({ ...prev, count: searchResults.length }))
            
        } catch (error) {
            console.error('Error searching:', error)
            setError(
                error?.error ||
                error?.message ||
                'Search failed. Please try again.'
            )
        } finally {
            setSearchLoading(false)
        }
    }, [searchValue])
    
    useEffect(() => {
        if (searchValue.trim()) {
            const timeoutId = setTimeout(() => {
                fetchSearchedValue()
            }, 500) // Debounce search
            return () => clearTimeout(timeoutId)
        } else {
            getData()
        }
    }, [tableConfig.page, tableConfig.rowsPerPage, searchValue])

    const columns = [
        {
            name: 'memberId',
            label: 'Member Number',
            options: {
                filter: true,
                customBodyRenderLite: (dataIndex) => {
                    const user = userList[dataIndex]
                    return (
                        <Typography variant="body2" fontWeight="medium">
                            {user?.memberId || 'N/A'}
                        </Typography>
                    )
                }
            },
        },
        {
            name: 'firstName',
            label: 'First Name',
            options: {
                filter: true,
                customBodyRenderLite: (dataIndex) => {
                    const user = userList[dataIndex]
                    return (
                        <Typography variant="body2">
                            {user?.firstName || 'N/A'}
                        </Typography>
                    )
                }
            },
        },
        {
            name: 'lastName',
            label: 'Last Name',
            options: {
                filter: true,
                customBodyRenderLite: (dataIndex) => {
                    const user = userList[dataIndex]
                    return (
                        <Typography variant="body2">
                            {user?.lastName || 'N/A'}
                        </Typography>
                    )
                }
            },
        },
        {
            name: 'membershiptypeLongName',
            label: 'Membership Type',
            options: {
                filter: true,
                customBodyRenderLite: (dataIndex) => {
                    const user = userList[dataIndex]
                    return (
                        <Chip
                            label={user?.membershiptypeLongName || 'Unknown'}
                            size="small"
                            variant="outlined"
                        />
                    )
                }
            },
        },
        {
            name: 'email',
            label: 'Email',
            options: {
                filter: true,
                customBodyRenderLite: (dataIndex) => {
                    const user = userList[dataIndex]
                    return (
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                wordBreak: 'break-word',
                                maxWidth: '200px' 
                            }}
                        >
                            {user?.email || 'No email provided'}
                        </Typography>
                    )
                }
            },
        },
        {
            name: 'has_email',
            label: 'Email Status',
            options: {
                filter: true,
                customBodyRenderLite: (dataIndex) => {
                    const user = userList[dataIndex]
                    
                    return (
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                            {!user?.account_health?.has_email ? (
                                <Chip
                                    label="Missing Email"
                                    size="small"
                                    color="warning"
                                    variant="filled"
                                    icon={<EmailIcon />}
                                />
                            ) : (
                                <Chip
                                    label="Has Email"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    icon={<EmailIcon />}
                                />
                            )}
                        </Box>
                    )
                }
            },
        },
        {
            name: 'has_assets',
            label: 'Asset Status',
            options: {
                filter: true,
                customBodyRenderLite: (dataIndex) => {
                    const user = userList[dataIndex]
                    
                    return (
                        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                            {!user?.account_health?.has_assets ? (
                                <Chip
                                    label="No Assets"
                                    size="small"
                                    color="error"
                                    variant="filled"
                                    icon={<AccountBalanceIcon />}
                                />
                            ) : (
                                <Chip
                                    label="Has Assets"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    icon={<AccountBalanceIcon />}
                                />
                            )}
                        </Box>
                    )
                }
            },
        },
        {
            name: 'action',
            label: 'Actions',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const user = userList[dataIndex]
                    return (
                        <FlexBox sx={{ gap: 1 }}>
                            <Tooltip title="Edit Profile" arrow>
                                <Link to={`/pages/new-customer/${user?._id}`}>
                                    <IconButton size="small" color="primary">
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </Link>
                            </Tooltip>
                            
                            <Tooltip title="View Profile" arrow>
                                <Link to={`/pages/view-customer/${user?._id}`}>
                                    <IconButton size="small" color="primary">
                                        <Icon>visibility</Icon>
                                    </IconButton>
                                </Link>
                            </Tooltip>
                        </FlexBox>
                    )
                },
            },
        },
    ]

    if (loading && !userList.length) {
        return (
            <Container>
                <div className="breadcrumb">
                    <Breadcrumb
                        routeSegments={[
                            { name: 'Pages', path: '/' },
                            { name: 'Members Awaiting Completion' },
                        ]}
                    />
                </div>
                <Box sx={{ mt: 3 }}>
                    <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="rectangular" height={400} />
                </Box>
            </Container>
        )
    }

    return (
        <Container>
            <div className="breadcrumb">
                <Breadcrumb
                    routeSegments={[
                        { name: 'Pages', path: '/' },
                        { name: 'Members Awaiting Completion' },
                    ]}
                />
            </div>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}


            <Box overflow="auto">
                <Box minWidth={750}>
                    <MUIDataTable
                        title={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6">
                                    Members Awaiting Completion
                                </Typography>
                                {(loading || searchLoading) && (
                                    <CircularProgress size={20} />
                                )}
                            </Box>
                        }
                        data={userList}
                        columns={columns}
                        options={{
                            filterType: 'checkbox',
                            draggableColumns: {
                                enabled: true,
                                transitionTime: 300
                            },
                            responsive: 'vertical',
                            jumpToPage: true,
                            count: tableConfig.count,
                            selectableRows: 'none',
                            filter: true,
                            print: true,
                            pagination: true,
                            download: true,
                            downloadOptions: {
                                filename: 'members-await-completion.csv',
                                separator: ',',
                                filterOptions: { useDisplayedColumnsOnly: true },
                                useDisplayedRowsOnly: true,
                            },
                            viewColumns: true,
                            serverSide: true,
                            elevation: 2,
                            search: true,
                            searchPlaceholder: 'Search by Member Number, Barcode or Email',
                            onSearchOpen: () => {
                                setSearchValue('')
                                setError(null)
                            },
                            onSearchClose: () => {
                                setSearchValue('')
                                setError(null)
                                getData()
                            },
                            rowsPerPage: tableConfig.rowsPerPage,
                            rowsPerPageOptions: [10, 20, 40, 80, 100],
                            customSearchRender: (searchText, handleSearch, hideSearch, options) => {
                                return (
                                    <Grow appear in={true} timeout={300}>
                                        <form onSubmit={(e) => {
                                            e.preventDefault()
                                            if (searchValue.trim()) {
                                                fetchSearchedValue()
                                            }
                                        }}>
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                value={searchValue}
                                                placeholder="Search by Member Number, Barcode or Email"
                                                onChange={({ target: { value } }) => setSearchValue(value)}
                                                InputProps={{
                                                    style: { paddingRight: 0 },
                                                    startAdornment: (
                                                        <Icon fontSize="small" sx={{ mr: 1 }}>
                                                            search
                                                        </Icon>
                                                    ),
                                                    endAdornment: (
                                                        <FlexBox>
                                                            {searchLoading && (
                                                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                                            )}
                                                            <IconButton onClick={hideSearch} size="small">
                                                                <Icon fontSize="small">clear</Icon>
                                                            </IconButton>
                                                        </FlexBox>
                                                    ),
                                                }}
                                            />
                                        </form>
                                    </Grow>
                                )
                            },
                            onChangePage: (page) => {
                                setTableConfig(prev => ({ ...prev, page }))
                            },
                            onChangeRowsPerPage: (rowsPerPage) => {
                                setTableConfig(prev => ({ ...prev, rowsPerPage, page: 0 }))
                            },
                            textLabels: {
                                body: {
                                    noMatch: searchValue 
                                        ? "No members found matching your search criteria"
                                        : "No members awaiting completion found",
                                }
                            }
                        }}
                    />
                </Box>
            </Box>
        </Container>
    )
}

export default CustomerAwaitingList